
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AIPersonality, AI_PERSONALITY_QUERY_KEY } from "@/components/settings/AIPersonalityPanel";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UseAIPersonalityOptions {
  includeUserName?: boolean;
  enabled?: boolean;
}

interface AIPersonalityResult {
  aiPersonality: AIPersonality;
  userName?: string;
  isLoading: boolean;
  updatePersonality: (personality: AIPersonality) => void;
  isUpdating: boolean;
}

interface UserSettingsData {
  ai_personality?: string;
  display_name?: string;
}

export function useAIPersonality(options: UseAIPersonalityOptions = {}): AIPersonalityResult {
  const { includeUserName = false, enabled = true } = options;
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: [AI_PERSONALITY_QUERY_KEY, user?.id, includeUserName],
    queryFn: async () => {
      if (!user) return null;
      
      const fieldsToSelect = includeUserName ? 'ai_personality,display_name' : 'ai_personality';
      
      console.log(`useAIPersonality: Fetching AI personality${includeUserName ? ' and username' : ''}`);
      const { data, error } = await supabase
        .from('user_settings')
        .select(fieldsToSelect)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error("useAIPersonality: Error fetching user settings:", error);
        throw error;
      }
      
      return data as UserSettingsData | null;
    },
    enabled: !!user && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes to reduce refetch noise
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const updateMutation = useMutation({
    mutationFn: async (personality: AIPersonality) => {
      if (!user) throw new Error("User not authenticated");
      
      if (import.meta.env.DEV && localStorage.getItem('debugPersonalityLogs') === 'true') {
        console.log(`useAIPersonality: Updating AI personality to ${personality}`);
      }
      
      const { data: existingSettings, error: checkError } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (checkError && checkError.code !== 'PGRST116') throw checkError;
      
      if (existingSettings) {
        const { error } = await supabase
          .from('user_settings')
          .update({ ai_personality: personality })
          .eq('user_id', user.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_settings')
          .insert({ user_id: user.id, ai_personality: personality });
        
        if (error) throw error;
      }
      
      return personality;
    },
    onSuccess: (personality) => {
      // Invalidate all related queries immediately
      queryClient.invalidateQueries({ queryKey: [AI_PERSONALITY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      queryClient.invalidateQueries({ queryKey: ['userNameForMessage'] });
      queryClient.invalidateQueries({ queryKey: ['userDisplayName'] });
      
      // Also refetch the current query to ensure immediate update
      queryClient.refetchQueries({ queryKey: [AI_PERSONALITY_QUERY_KEY, user?.id] });
      
      if (import.meta.env.DEV && localStorage.getItem('debugPersonalityLogs') === 'true') {
        console.log(`useAIPersonality: Successfully updated to ${personality}`);
      }
      
      toast({
        title: "AI Personality Updated",
        description: `AI personality set to ${
          personality === 'ultraterrestrial' ? 'Ultraterrestrial' : 
          personality === 'terrestrial' ? 'Terrestrial' : 
          personality === 'passenger' ? 'Passenger' : 
          personality === 'static' ? 'Static' :
          personality === 'librarian' ? 'Librarian' :
          'Entity #247B'
        }`
      });
    },
    onError: (error: Error) => {
      console.error("useAIPersonality: Error updating AI personality:", error);
      toast({
        title: "Error",
        description: `Failed to update setting: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  const aiPersonality = (settings?.ai_personality as AIPersonality) || 'ultraterrestrial';
  const userName = includeUserName ? settings?.display_name : undefined;

  

  return {
    aiPersonality,
    userName,
    isLoading,
    updatePersonality: updateMutation.mutate,
    isUpdating: updateMutation.isPending
  };
}
